import * as Phaser from "phaser"
import {
  BUMPER_SIZE_DEFS,
  GAME_HEIGHT,
  GAME_WIDTH,
  SCENE_TUNING,
} from "../data/constants"
import type {
  BumperEffectType,
  CoinEntityData,
  CoinMagnetState,
  GameData,
  GameSprite,
} from "../types"
import type { BumperSystem } from "./bumper-system"
import type { VfxManager } from "./vfx-manager"

interface FieldRuntimeOptions {
  projectileGroup: Phaser.Physics.Arcade.Group
  expCoinGroup: Phaser.Physics.Arcade.Group
  bumperGroup: Phaser.Physics.Arcade.Group
  bumperSystem: BumperSystem
  vfx: VfxManager
  getGameData: () => GameData
  getDogSprite: () => GameSprite
  getProjectileEffectColor: (
    projectile: Extract<GameSprite["entityData"], { kind: "projectile" }>,
  ) => number
  onHandleBumperHit: (bumper: GameSprite) => void
}

const BUMPER_VISUAL_TUNING: Record<
  BumperEffectType,
  { pulse: number; phase: number; angle: number }
> = {
  charge: { pulse: 1, phase: 0, angle: 0 },
  guard: { pulse: 0.9, phase: 120, angle: -2 },
  bank: { pulse: 1.2, phase: 40, angle: 5 },
  heal: { pulse: 1.05, phase: 220, angle: -4 },
  levelup: { pulse: 0.8, phase: 180, angle: 0 },
}

export class FieldRuntime {
  private readonly options: FieldRuntimeOptions

  constructor(options: FieldRuntimeOptions) {
    this.options = options
  }

  public updateProjectiles(delta: number): void {
    for (const child of this.options.projectileGroup.children) {
      const sprite = child as GameSprite
      const projectile = sprite.entityData
      if (!sprite.active || !projectile || projectile.kind !== "projectile")
        continue

      sprite.x += projectile.vx * (delta / 16)
      sprite.y += projectile.vy * (delta / 16)
      projectile.x = sprite.x
      projectile.y = sprite.y

      sprite.setRotation(Math.atan2(projectile.vy, projectile.vx))

      const trailColor = this.options.getProjectileEffectColor(projectile)
      if (projectile.isEnemy && Math.random() > 0.75) {
        this.options.vfx.emitProjectileTrail(
          sprite.x - 4,
          sprite.y,
          trailColor,
          1,
        )
      }

      if (
        sprite.x < -SCENE_TUNING.projectileCleanupMargin ||
        sprite.x > GAME_WIDTH + SCENE_TUNING.projectileCleanupMargin ||
        sprite.y < -SCENE_TUNING.projectileCleanupMargin ||
        sprite.y > GAME_HEIGHT + SCENE_TUNING.projectileCleanupMargin
      ) {
        sprite.destroy()
      }
    }
  }

  public updateExpCoins(delta: number, waveProgress: number): void {
    const gameData = this.options.getGameData()
    const dogSprite = this.options.getDogSprite()
    const harvestMultiplier =
      waveProgress >= SCENE_TUNING.harvestMagnetStartProgress
        ? SCENE_TUNING.harvestMagnetRadiusMultiplier
        : 1
    const playerMagnetRange = SCENE_TUNING.expCoinAttractRadius
    const attractRadius = playerMagnetRange * harvestMultiplier
    const attractSpeed =
      SCENE_TUNING.expCoinAttractSpeed *
      (waveProgress >= SCENE_TUNING.harvestMagnetStartProgress
        ? SCENE_TUNING.harvestMagnetSpeedMultiplier
        : 1)

    for (const child of this.options.expCoinGroup.children) {
      const sprite = child as GameSprite
      if (!sprite.active) continue
      const coin = sprite.entityData as CoinEntityData | undefined
      if (!coin || coin.kind !== "coin") continue

      const cd = coin as CoinEntityData & { magnetState?: CoinMagnetState }

      sprite.x -=
        gameData.scrollSpeed *
        SCENE_TUNING.expCoinScrollFactor *
        (delta / SCENE_TUNING.frameNormalizationMs)

      // Skip player attraction if a harvester is already stealing this coin
      if (cd.stolenBy) {
        sprite.y +=
          Math.sin(
            sprite.x / SCENE_TUNING.expCoinFloatDivisor +
              sprite.y / SCENE_TUNING.expCoinFloatDivisor,
          ) * SCENE_TUNING.expCoinFloatAmplitude
        if (sprite.x < SCENE_TUNING.expCoinCleanupX) {
          sprite.destroy()
        }
        continue
      }

      const dx = dogSprite.x - sprite.x
      const dy = dogSprite.y - sprite.y
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (dist < attractRadius && dist > 0) {
        const factor = (attractRadius - dist) / attractRadius

        if (!cd.magnetState || !cd.magnetState.isMagnetized) {
          cd.magnetState = {
            magnetPath: null,
            magnetProgress: 0,
            isMagnetized: true,
            startX: sprite.x,
            startY: sprite.y,
          }
          cd.magnetState.magnetPath = new Phaser.Curves.Path(sprite.x, sprite.y)
          cd.magnetState.magnetPath.quadraticBezierTo(
            dogSprite.x,
            dogSprite.y,
            dogSprite.x - 30,
            (sprite.y + dogSprite.y) / 2,
          )
        }

        if (cd.magnetState.magnetPath && cd.magnetState.magnetProgress < 1) {
          cd.magnetState.magnetProgress += (attractSpeed / 200) * (delta / 16)
          const pt = cd.magnetState.magnetPath.getPoint(
            Math.min(1, cd.magnetState.magnetProgress),
          )
          sprite.x = pt.x
          sprite.y = pt.y
        } else {
          sprite.x += (dx / dist) * attractSpeed * factor
          sprite.y += (dy / dist) * attractSpeed * factor
        }
      } else {
        if (cd.magnetState) {
          cd.magnetState.isMagnetized = false
          cd.magnetState.magnetProgress = 0
        }
      }

      sprite.y +=
        Math.sin(
          sprite.x / SCENE_TUNING.expCoinFloatDivisor +
            sprite.y / SCENE_TUNING.expCoinFloatDivisor,
        ) * SCENE_TUNING.expCoinFloatAmplitude

      if (sprite.x < SCENE_TUNING.expCoinCleanupX) {
        sprite.destroy()
      }
    }
  }

  public updateBumpers(time: number, delta: number): void {
    const gameData = this.options.getGameData()
    const dogSprite = this.options.getDogSprite()

    for (const child of this.options.bumperGroup.children) {
      const sprite = child as GameSprite
      const bumper = sprite.entityData
      if (!sprite.active || !bumper || bumper.kind !== "bumper") continue

      const speedRatio = this.options.bumperSystem.getBumperSpeedRatio(bumper)
      const vx =
        -(gameData.scrollSpeed * SCENE_TUNING.bumperVelocityBaseScale) *
        speedRatio
      sprite.x += vx * (delta / 16)

      const seekY = Phaser.Math.Clamp(
        dogSprite.y + bumper.targetYOffset,
        SCENE_TUNING.bumperSeekMarginY,
        GAME_HEIGHT - SCENE_TUNING.bumperSeekMarginY,
      )
      sprite.y = Phaser.Math.Linear(
        sprite.y,
        seekY,
        SCENE_TUNING.bumperSeekLerp * speedRatio,
      )
      sprite.y +=
        Math.sin(
          (time + bumper.wobbleSeed) / SCENE_TUNING.bumperWobbleDivisor,
        ) *
        SCENE_TUNING.bumperWobbleAmplitude *
        speedRatio

      bumper.x = sprite.x
      bumper.y = sprite.y

      if (sprite.x < SCENE_TUNING.bumperCleanupX) {
        sprite.destroy()
        continue
      }

      const sizeDef = BUMPER_SIZE_DEFS[bumper.size]
      const baseScale = sizeDef.radius / (bumper.radius || sizeDef.radius)
      if (bumper.stunTimer > 0) {
        const flash = Math.sin(time * 0.02) > 0
        sprite.setAlpha(flash ? 0.6 : 1)
        sprite.setScale(baseScale)
      } else {
        sprite.setAlpha(1)
        const visual = BUMPER_VISUAL_TUNING[bumper.effect]
        const scale =
          baseScale +
          Math.sin((time + visual.phase) / SCENE_TUNING.bumperPulseDivisor) *
            SCENE_TUNING.bumperPulseAmount *
            visual.pulse
        sprite.setScale(scale)
        sprite.setAngle(Math.sin((time + visual.phase) / 260) * visual.angle)
      }
    }
  }

  public checkBumperCollisions(): void {
    const dogSprite = this.options.getDogSprite()

    for (const child of this.options.bumperGroup.children) {
      const bumper = child as GameSprite
      const bumperData = bumper.entityData
      if (!bumper.active || !bumperData || bumperData.kind !== "bumper")
        continue

      const dx = dogSprite.x - bumper.x
      const dy = dogSprite.y - bumper.y
      const distance = Math.sqrt(dx * dx + dy * dy)
      const contactDistance = (bumperData.radius || 30) + 35

      if (
        this.options.bumperSystem.canTriggerPlayerContact(
          bumper,
          distance,
          contactDistance,
        )
      ) {
        this.options.onHandleBumperHit(bumper)
      }
    }
  }
}
